import React, { FC } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CodeIcon from '@mui/icons-material/Code';
import { tokens } from '../../theme';
import {
  WebinarAffiliateGroupingOverview,
  AffiliateGroupingCategory,
} from '../../helpers/webinarStatisticsData';

interface AffiliateGroupingsTabProps {
  colors: ReturnType<typeof tokens>;
  data: WebinarAffiliateGroupingOverview | null;
}

const AffiliateGroupingsTab: FC<AffiliateGroupingsTabProps> = ({ colors, data }) => {
  const theme = useTheme();

  const renderGroupingCard = (grouping: AffiliateGroupingCategory) => (
    <Accordion
      key={grouping.category}
      sx={{
        backgroundColor: colors.primary[400],
        mb: 2,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" fontWeight="600">
            {grouping.category}
          </Typography>
          <Chip
            label={`${grouping.count.toLocaleString('de-DE')} Registrierungen`}
            color="primary"
            size="small"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {/* Rules Section */}
          <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
            <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Regeln
          </Typography>
          <Box sx={{ mb: 3 }}>
            {grouping.patterns.map((pattern, idx) => (
              <Chip
                key={idx}
                label={pattern}
                variant="outlined"
                sx={{
                  mr: 1,
                  mb: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                }}
              />
            ))}
          </Box>

          {/* Examples Section */}
          <Typography variant="h6" fontWeight="600" sx={{ mb: 1 }}>
            Beispiele ({grouping.examples.length})
          </Typography>
          <Box>
            {grouping.examples.map((example, idx) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  backgroundColor: theme.palette.mode === 'dark' ? colors.primary[500] : '#f5f5f5',
                  color: theme.palette.mode === 'dark' ? colors.grey[100] : '#333',
                  p: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  wordBreak: 'break-all',
                }}
              >
                {example || '(leer)'}
              </Typography>
            ))}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
        <Typography color={colors.grey[300]} variant="h5">
          Klicken Sie auf &quot;Laden&quot; um die Daten zu laden
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Ad-Type Groupings */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" fontWeight="600" sx={{ color: colors.grey[100], mb: 2 }}>
          Ad-Type Gruppierung
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, color: colors.grey[300] }}>
          Kategorisierung nach Akquisitionskanal (basierend auf dem Affiliate-Feld). In den Auswertungen zählt je Person der Kanal ihrer ersten Anmeldung zu einem Webinar.
        </Typography>
        {data.adType.map((grouping) => renderGroupingCard(grouping))}
      </Box>

      {/* Blog Groupings */}
      <Box>
        <Typography variant="h3" fontWeight="600" sx={{ color: colors.grey[100], mb: 2 }}>
          Blog Gruppierung
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, color: colors.grey[300] }}>
          Kategorisierung nach Quell-Blog/Liste (basierend auf dem Affiliate-Feld). In den Auswertungen zählt je Person der Blog ihrer ersten Anmeldung zu einem Webinar.
        </Typography>
        {data.blog.map((grouping) => renderGroupingCard(grouping))}
      </Box>
    </Box>
  );
};

export default AffiliateGroupingsTab;
